import { FC, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCreateUserWithEmailAndPassword } from "react-firebase-hooks/auth";
import {
  GoogleAuthProvider,
  getAdditionalUserInfo,
  browserLocalPersistence,
  setPersistence,
  signInWithPopup,
} from "firebase/auth";
import { firebaseAuth } from "../../firebaseConfig";
import { useUserSession } from "../../context/UserContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { SignupForm } from "@components";
import { ValidationError } from "../../types";

const authSuccess: ValidationError = { isError: false };

export const SignUpPage: FC = () => {
  const [createUserWithEmailAndPassword, user, loading, error] =
    useCreateUserWithEmailAndPassword(firebaseAuth);

  const [errors, setErrors] = useState(authSuccess);
  const [isGoogleProcessing, setIsGoogleProcessing] = useState(false);
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { createUserSession, setUserSession, persistUserSession } =
    useUserSession();
  const { setActiveWorkspace } = useWorkspace();

  useEffect(() => {
    if (user) {
      var userSession = createUserSession(user?.user);
      if (userSession) {
        setUserSession(userSession);
        if (userSession) persistUserSession(userSession);
      }
    }

    if (error) {
      var authResult: ValidationError = {
        isError: true,
        errorFields: [],
        errorMessage: t("errors.signup_failure"),
      };
      if (error.code === "auth/invalid-email") {
        authResult.errorFields = ["email"];
        authResult.errorMessage = t("errors.email_invalid");
      }
      if (error.code === "auth/email-already-in-use") {
        authResult.errorFields = ["email"];
        authResult.errorMessage = t("errors.email_already_in_use");
      }
      if (error.code === "auth/missing-password") {
        authResult.errorFields = ["password"];
        authResult.errorMessage = t("errors.wrong_password");
      }
      if (error.code === "auth/weak-password") {
        authResult.errorFields = ["password"];
        authResult.errorMessage = t("errors.password_too_weak");
      }
      setErrors(authResult);
    }
  }, [user, error, setUserSession, createUserSession, persistUserSession, t]);

  const handleGoogleSignup = async () => {
    setErrors(authSuccess);
    setIsGoogleProcessing(true);
    setActiveWorkspace(undefined);

    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const credential = await signInWithPopup(
        firebaseAuth,
        new GoogleAuthProvider(),
      );
      const userSession = createUserSession(credential.user);
      if (userSession) {
        setUserSession(userSession);
        persistUserSession(userSession);
      }
      // Navigate imperatively (matching the working login flow) instead of
      // relying on a render-time <Navigate>. Existing Google accounts go
      // straight to their workspace; only brand-new accounts hit onboarding.
      navigate(
        getAdditionalUserInfo(credential)?.isNewUser
          ? "/load_workspace?from=signup"
          : "/load_workspace",
      );
    } catch (error: any) {
      console.error("[google-signup] failed:", error?.code, error?.message);
      if (
        error?.code === "auth/popup-closed-by-user" ||
        error?.code === "auth/cancelled-popup-request"
      ) {
        setErrors(authSuccess);
      } else {
        setErrors({
          isError: true,
          errorFields: [],
          errorMessage: t("errors.google_signup_failure"),
        });
      }
    } finally {
      setIsGoogleProcessing(false);
    }
  };

  // Email/password signups are always brand-new users → onboarding.
  // Google signups navigate imperatively in handleGoogleSignup.
  if (user) {
    return <Navigate to="/load_workspace?from=signup" />;
  }

  return (
    <div className="bg-surface-muted w-screen h-screen">
      <SignupForm
        onClicksignup={async (email: string, password: string) =>
          {
            setActiveWorkspace(undefined);
            return createUserWithEmailAndPassword(email, password);
          }
        }
        onClickGoogleSignup={handleGoogleSignup}
        validation={errors}
        isProcessing={loading || isGoogleProcessing}
      />
    </div>
  );
};
